import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';

const line = {
  hidden: { y: '110%' },
  visible: (i = 0) => ({
    y: '0%',
    transition: {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1],
      delay: 0.2 + i * 0.12,
    },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: 'easeOut', delay: 0.9 + i * 0.1 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src="https://images.unsplash.com/photo-1579441982815-be67da231afb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjb3Jwb3JhdGUlMjBhcmNoaXRlY3R1cmUlMjBibGFja3xlbnwwfHx8fDE3ODQwNDg5NDN8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/50 via-[#050505]/70 to-[#050505]" />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 70% 30%, rgba(212,175,55,0.10) 0%, transparent 60%)'
        }} />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 pt-40 lg:pt-48 pb-24 min-h-[100svh] flex flex-col justify-center"
      >
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="flex items-center gap-4 mb-10 lg:mb-14"
          data-testid="hero-overline"
        >
          <span className="w-10 h-px bg-[#c2a978]" />
          <span className="text-[10px] tracking-[0.5em] uppercase text-[#c2a978] font-sans">
            Est. — Private Capital Group
          </span>
        </motion.div>

        {/* Main headline — masked line-by-line reveal */}
        <h1 className="font-serif text-[#f5f5f0] leading-[0.92] tracking-[-0.02em]">
          <span className="line-mask">
            <motion.span
              variants={line}
              initial="hidden"
              animate="visible"
              custom={0}
              className="block text-[6.5vw] lg:text-[4.5vw]"
            >
              <span className="italic text-[#c2a978]">Capital</span>{' '}
              <span className="text-[#f5f5f0]">for</span>
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span
              variants={line}
              initial="hidden"
              animate="visible"
              custom={1}
              className="block text-[6.5vw] lg:text-[4.5vw]"
            >
              the <span className="italic">bold</span>,
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span
              variants={line}
              initial="hidden"
              animate="visible"
              custom={2}
              className="block text-[6.5vw] lg:text-[4.5vw]"
            >
              the <span className="italic">underestimated</span>,
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span
              variants={line}
              initial="hidden"
              animate="visible"
              custom={3}
              className="block text-[6.5vw] lg:text-[4.5vw] text-[#8a8a8a]"
            >
              the <span className="italic text-[#c2a978]">relentless</span>.
            </motion.span>
          </span>
        </h1>

        {/* Sub row */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="visible"
            custom={0}
            className="lg:col-span-5 text-[#8a8a8a] text-sm lg:text-base leading-relaxed max-w-md"
            data-testid="hero-subtext"
          >
            Scorpion Funding deploys discreet, high-velocity capital to
            operators who move faster than the market. From{' '}
            <span className="text-[#f5f5f0]">$25K</span> to{' '}
            <span className="text-[#f5f5f0]">$10M+</span> — approved in as
            little as{' '}
            <span className="text-[#c2a978]">3 hours</span>.
          </motion.p>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="visible"
            custom={1}
            className="lg:col-span-4 flex flex-wrap gap-4"
          >
            <a href="https://app.scorpionfunding.com" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="hero-cta-apply">
              Request Capital <ArrowUpRight size={16} />
            </a>
            <a href="#services" className="btn-ghost" data-testid="hero-cta-explore">
              Explore Programs
            </a>
          </motion.div>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="visible"
            custom={2}
            className="lg:col-span-3 lg:justify-self-end text-right"
          >
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#8a8a8a] mb-2">
              Deployed to date
            </div>
            <div className="font-serif italic text-2xl lg:text-3xl text-[#c2a978]">
              $725M+
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom cursor & scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 lg:left-12 z-10 flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-[#8a8a8a]"
        data-testid="hero-scroll-cue"
      >
        <span className="w-2 h-2 bg-[#c2a978] blink" />
        Scroll · Deeper
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-12 z-10 text-[10px] tracking-[0.4em] uppercase text-[#8a8a8a]"
      >
        (01 / 09)
      </motion.div>
    </section>
  );
}
